import { createSelector } from "@reduxjs/toolkit";
import { Base } from "../../framework/base";
import { PlannedWorkShiftDto } from "../../models/plannedWorkShift/plannedWorkShift";
import { RootState } from "../../framework/customStore";

type ConflictEntry = {
    employeeConflicts: string[];
    vehicleConflicts: string[];
};
type ConflictMap = Record<string, ConflictEntry>;

const selectPlannedShifts = (state: RootState): PlannedWorkShiftDto[] =>
    state.workShiftPlanning?.plannedWorkShifts ?? [];

// Validate single interval
const getInterval = (s: PlannedWorkShiftDto) => {
    const start = s.startTime;
    const end = s.endTime;
    const valid = !!start && !!end && Base.isAfter(end, start);
    return { start, end, valid };
};

// Simple pairwise overlap detection within a single group
const buildConflictsForGroup = (group: PlannedWorkShiftDto[]): Record<string, string[]> => {
    const items = group
        .map((s) => {
            const { start, end, valid } = getInterval(s);
            return { id: s.id, start, end, valid };
        })
        .filter((x) => x.valid);

    const pairs: Record<string, Set<string>> = {};
    for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
            const a = items[i];
            const b = items[j];
            // Half-open overlap: [start, end), touching edges are NOT overlapping
            if (Base.areIntervalsOverlappingHalfOpen(a.start, a.end, b.start, b.end)) {
                (pairs[a.id] ??= new Set()).add(b.id);
                (pairs[b.id] ??= new Set()).add(a.id);
            }
        }
    }

    const res: Record<string, string[]> = {};
    Object.entries(pairs).forEach(([id, set]) => (res[id] = Array.from(set)));
    return res;
};

/**
 * Builds a conflict map per shift:
 * - employeeConflicts: overlaps with same employeeId
 * - vehicleConflicts: overlaps with same vehicleId
 */
export const selectShiftOverlapsById = createSelector(
    [selectPlannedShifts],
    (shifts): ConflictMap => {
        const byEmployee: Record<string, PlannedWorkShiftDto[]> = {};
        const byVehicle: Record<string, PlannedWorkShiftDto[]> = {};
        const result: ConflictMap = {};

        // Seed result and group by resource
        for (const s of shifts) {
            result[s.id] = { employeeConflicts: [], vehicleConflicts: [] };
            if (s.employeeId) (byEmployee[s.employeeId] ??= []).push(s);
            if (s.vehicleId) (byVehicle[s.vehicleId] ??= []).push(s);
        }

        // Employee conflicts
        Object.values(byEmployee).forEach((group) => {
            const map = buildConflictsForGroup(group);
            for (const [id, ids] of Object.entries(map)) {
                result[id].employeeConflicts = ids;
            }
        });

        // Vehicle conflicts
        Object.values(byVehicle).forEach((group) => {
            const map = buildConflictsForGroup(group);
            for (const [id, ids] of Object.entries(map)) {
                result[id].vehicleConflicts = ids;
            }
        });

        return result;
    }
);

export const selectEmployeeConflictIds = (state: RootState, shiftId: string): string[] =>
    selectShiftOverlapsById(state)[shiftId]?.employeeConflicts ?? [];

export const selectVehicleConflictIds = (state: RootState, shiftId: string): string[] =>
    selectShiftOverlapsById(state)[shiftId]?.vehicleConflicts ?? [];

export const selectHasAnyConflict = (state: RootState, shiftId: string): boolean => {
    const e = selectShiftOverlapsById(state)[shiftId];
    return !!e && ((e.employeeConflicts?.length ?? 0) > 0 || (e.vehicleConflicts?.length ?? 0) > 0);
};